/** @jsxImportSource theme-ui */
import React, { useState } from 'react';
import { Badge, Box, Flex, Heading, Text } from 'theme-ui';
import { Formik } from 'formik';
import {
    useGetFeatureFlagsQuery,
    usePostFeatureFlagMutation,
    usePutFeatureFlagMutation,
    useDeleteFeatureFlagMutation,
} from '@/store/apiService';
import { Container } from '@/components/styled';
import Button from '@/components/Button';
import { GrayFormikField } from '@/components/GrayFormikField';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { toast } from 'react-toastify';
import { FeatureFlagAdminResponse } from '@/types/types';

type View = 'list' | 'create';

const FLAG_NAME_MAX_LEN = 100;

const validateFlagName = (name: string): { name?: string } => {
    if (!name.trim()) return { name: 'Required' };
    if (name.length > FLAG_NAME_MAX_LEN) return { name: `Max ${FLAG_NAME_MAX_LEN} characters` };
    return {};
};

const FlagRow: React.FC<{
    flag: FeatureFlagAdminResponse;
    expanded: boolean;
    onToggle: () => void;
}> = ({ flag, expanded, onToggle }) => {
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [putFlag] = usePutFeatureFlagMutation();
    const [deleteFlag, deleteState] = useDeleteFeatureFlagMutation();

    return (
        <Box sx={{ mb: 3, borderRadius: 'sm', backgroundColor: 'gray.1', overflow: 'hidden' }}>
            <Flex
                sx={{
                    gap: 3,
                    p: 3,
                    cursor: 'pointer',
                    alignItems: 'center',
                    '&:hover': { backgroundColor: 'gray.2' },
                }}
                onClick={onToggle}
            >
                <Text sx={{ color: 'gray.5', fontSize: 0 }}>#{flag.Id}</Text>
                <Text sx={{ fontWeight: 'bold' }}>{flag.Name}</Text>
                <Badge
                    sx={{
                        variant: flag.IsActive ? 'badges.active' : undefined,
                        ...(flag.IsActive
                            ? {}
                            : { backgroundColor: 'gray.4', color: 'gray.7' }),
                        fontSize: 0,
                    }}
                >
                    {flag.IsActive ? 'Active' : 'Inactive'}
                </Badge>
                {flag.IsPublic && (
                    <Badge sx={{ backgroundColor: 'blue.4', fontSize: 0 }}>Public</Badge>
                )}
                {flag.CompanyAdminCanToggle && (
                    <Badge sx={{ backgroundColor: 'gray.5', fontSize: 0 }}>Admin toggle</Badge>
                )}
                <Text sx={{ color: 'gray.6', fontSize: 0, ml: 'auto' }}>
                    {flag.CreatedDate ? new Date(flag.CreatedDate).toLocaleDateString('sv-SE') : ''}
                </Text>
                <Text sx={{ fontSize: 1, color: 'gray.5' }}>{expanded ? '▲' : '▼'}</Text>
            </Flex>

            {expanded && (
                <Box sx={{ px: 3, pb: 3 }}>
                    {confirmDelete && (
                        <ConfirmationModal
                            hideOnClickOutside
                            loading={deleteState.isLoading}
                            headingText={`Delete "${flag.Name}"?`}
                            content="This will permanently delete the feature flag and all company overrides."
                            onNo={() => setConfirmDelete(false)}
                            onYes={() => {
                                deleteFlag({ Id: flag.Id! })
                                    .unwrap()
                                    .then(() => setConfirmDelete(false))
                                    .catch((err) => {
                                        setConfirmDelete(false);
                                        toast.error(
                                            err?.data?.ResponseStatus?.Message ??
                                                'Failed to delete feature flag',
                                            { autoClose: 5000 },
                                        );
                                    });
                            }}
                        />
                    )}
                    <Formik
                        key={flag.Id}
                        initialValues={{
                            name: flag.Name ?? '',
                            isActive: flag.IsActive ?? false,
                            isPublic: flag.IsPublic ?? false,
                            companyAdminCanToggle: flag.CompanyAdminCanToggle ?? false,
                        }}
                        validate={(values) => validateFlagName(values.name)}
                        onSubmit={(values, { setSubmitting }) => {
                            putFlag({
                                Id: flag.Id!,
                                Name: values.name,
                                IsActive: values.isActive,
                                IsPublic: values.isPublic,
                                CompanyAdminCanToggle: values.companyAdminCanToggle,
                            })
                                .unwrap()
                                .then(() => onToggle())
                                .catch((err) => {
                                    setSubmitting(false);
                                    toast.error(
                                        err?.data?.ResponseStatus?.Message ??
                                            'Failed to update feature flag',
                                        { autoClose: 5000 },
                                    );
                                });
                        }}
                    >
                        {(formProps) => (
                            <Flex sx={{ flexDirection: 'column', gap: 3, pt: 2 }}>
                                <GrayFormikField name="name" label="Name" required autoComplete="off" />
                                <GrayFormikField type="checkbox" name="isActive" label="Active" />
                                <GrayFormikField
                                    type="checkbox"
                                    name="isPublic"
                                    label="Public (platform-controlled)"
                                />
                                <GrayFormikField
                                    type="checkbox"
                                    name="companyAdminCanToggle"
                                    label="Company admin can toggle"
                                />
                                <Flex sx={{ justifyContent: 'space-between', gap: 2 }}>
                                    <Button
                                        variant="danger"
                                        type="button"
                                        onClick={() => setConfirmDelete(true)}
                                    >
                                        Delete
                                    </Button>
                                    <Flex sx={{ gap: 2 }}>
                                        <Button
                                            variant="secondary"
                                            type="button"
                                            onClick={onToggle}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            isLoading={formProps.isSubmitting}
                                            disabled={formProps.isSubmitting}
                                            onClick={formProps.submitForm}
                                        >
                                            Save
                                        </Button>
                                    </Flex>
                                </Flex>
                            </Flex>
                        )}
                    </Formik>
                </Box>
            )}
        </Box>
    );
};

const FeatureFlags: React.FC = () => {
    const [view, setView] = useState<View>('list');
    const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
    const { data, isLoading } = useGetFeatureFlagsQuery({});
    const [postFlag] = usePostFeatureFlagMutation();

    const flags = data?.Results ?? [];

    const toggleExpanded = (id: number) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    if (view === 'create') {
        return (
            <Container>
                <Flex sx={{ alignItems: 'center', gap: 2, mb: 3 }}>
                    <Button variant="secondary" type="button" onClick={() => setView('list')}>
                        Back
                    </Button>
                    <Heading as="h2">Create feature flag</Heading>
                </Flex>
                <Formik
                    initialValues={{
                        name: '',
                        isActive: true,
                        isPublic: false,
                        companyAdminCanToggle: false,
                    }}
                    validate={(values) => validateFlagName(values.name)}
                    onSubmit={(values, { setSubmitting }) => {
                        postFlag({
                            Name: values.name,
                            IsActive: values.isActive,
                            IsPublic: values.isPublic,
                            CompanyAdminCanToggle: values.companyAdminCanToggle,
                        })
                            .unwrap()
                            .then(() => setView('list'))
                            .catch((err) => {
                                setSubmitting(false);
                                toast.error(
                                    err?.data?.ResponseStatus?.Message ??
                                        'Failed to create feature flag',
                                    { autoClose: 5000 },
                                );
                            });
                    }}
                >
                    {(formProps) => (
                        <Flex sx={{ flexDirection: 'column', gap: 3 }}>
                            <GrayFormikField
                                name="name"
                                label="Name"
                                required
                                placeholder="my-feature-flag"
                                autoComplete="off"
                            />
                            <GrayFormikField type="checkbox" name="isActive" label="Active" />
                            <GrayFormikField
                                type="checkbox"
                                name="isPublic"
                                label="Public (platform-controlled)"
                            />
                            <GrayFormikField
                                type="checkbox"
                                name="companyAdminCanToggle"
                                label="Company admin can toggle"
                            />
                            <Flex sx={{ justifyContent: 'flex-end', gap: 2 }}>
                                <Button
                                    variant="secondary"
                                    type="button"
                                    onClick={() => setView('list')}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    isLoading={formProps.isSubmitting}
                                    disabled={formProps.isSubmitting}
                                    onClick={formProps.submitForm}
                                >
                                    Save
                                </Button>
                            </Flex>
                        </Flex>
                    )}
                </Formik>
            </Container>
        );
    }

    return (
        <Container>
            <Flex sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Heading as="h2">Feature flags</Heading>
                <Button onClick={() => setView('create')}>Create flag</Button>
            </Flex>
            {isLoading && <Text>Loading...</Text>}
            {!isLoading && flags.length === 0 && <Text>No feature flags found.</Text>}
            {flags.map((flag) => (
                <FlagRow
                    key={flag.Id}
                    flag={flag}
                    expanded={expandedIds.has(flag.Id!)}
                    onToggle={() => toggleExpanded(flag.Id!)}
                />
            ))}
        </Container>
    );
};

export default FeatureFlags;
